import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { useCorporateEnrollment } from '@/hooks/useCorporateEnrollment'
import { useCorpStore } from '@/reducers/corporateReducer'
import { uploadCorporateDocuments } from '@/requests/corporate'

import { ROUTES } from '../CorporateSignupPanel'
import * as Select from '@radix-ui/react-select'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import clsx from 'clsx'

interface ValidationError {
  issuer?: string
  file?: string
  submit?: string
}

const NON_CANADIAN_ISSUER_VALUE = 'non_canadian'

const CANADIAN_ISSUERS = [
  {
    group: 'Federal Government',
    options: [{ value: 'federal', label: 'Government of Canada' }],
  },
  {
    group: 'Provincial Government',
    options: [
      { value: 'alberta', label: 'Alberta' },
      { value: 'british_columbia', label: 'British Columbia' },
      { value: 'manitoba', label: 'Manitoba' },
      { value: 'new_brunswick', label: 'New Brunswick' },
      { value: 'newfoundland', label: 'Newfoundland and Labrador' },
      { value: 'nova_scotia', label: 'Nova Scotia' },
      { value: 'ontario', label: 'Ontario' },
      { value: 'pei', label: 'Prince Edward Island' },
      { value: 'quebec', label: 'Quebec' },
      { value: 'saskatchewan', label: 'Saskatchewan' },
      { value: 'nwt', label: 'Northwest Territories' },
      { value: 'nunavut', label: 'Nunavut' },
      { value: 'yukon', label: 'Yukon' },
    ],
  },
]

const NON_CANADIAN_ISSUERS = [
  {
    group: 'Issuer',
    options: [{ value: NON_CANADIAN_ISSUER_VALUE, label: 'Non-Canadian' }],
  },
]

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

export default function CorporateIssuerDocuments() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const formCountry = useCorpStore((state) => state.country)
  const { data: enrollment, isFetchedAfterMount } = useCorporateEnrollment(true)
  const savedCountry = isFetchedAfterMount
    ? enrollment?.address?.country?.toUpperCase()
    : undefined
  const country = savedCountry ?? formCountry
  const savedIssuer = useCorpStore((state) => state.incorporationIssuer)
  const setIncorporationIssuer = useCorpStore(
    (state) => state.setIncorporationIssuer
  )
  const setCertificateObjectName = useCorpStore(
    (state) => state.setCertificateObjectName
  )
  const isNonCanadian = country !== 'CA'
  const [file, setFile] = useState<File | null>(null)
  const [selectedIssuer, setSelectedIssuer] = useState<string>('')
  const [isDragging, setIsDragging] = useState(false)
  const [errors, setErrors] = useState<ValidationError>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const issuerGroups = isNonCanadian ? NON_CANADIAN_ISSUERS : CANADIAN_ISSUERS

  useEffect(() => {
    if (isNonCanadian) {
      setSelectedIssuer(NON_CANADIAN_ISSUER_VALUE)
      return
    }
    setSelectedIssuer(savedIssuer)
  }, [isNonCanadian, savedIssuer])

  const validateForm = (): ValidationError => {
    const newErrors: ValidationError = {}

    if (!selectedIssuer) {
      newErrors.issuer = t('corporateIssuerUpload.errors.issuerRequired')
    }

    if (!file) {
      newErrors.file = t('corporateIssuerUpload.errors.fileRequired')
    } else {
      if (file.size > MAX_FILE_SIZE) {
        newErrors.file = t('corporateIssuerUpload.errors.fileSizeExceeded')
      }
      if (!isValidFileType(file)) {
        newErrors.file = t('corporateIssuerUpload.errors.invalidFileType')
      }
    }

    return newErrors
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    setTouched((prev) => ({ ...prev, file: true }))

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      if (droppedFile.size > MAX_FILE_SIZE) {
        setErrors((prev) => ({
          ...prev,
          file: t('corporateIssuerUpload.errors.fileSizeExceeded'),
        }))
        return
      }
      if (isValidFileType(droppedFile)) {
        setFile(droppedFile)
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors.file
          return newErrors
        })
      } else {
        setErrors((prev) => ({
          ...prev,
          file: t('corporateIssuerUpload.errors.invalidFileType'),
        }))
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    setTouched((prev) => ({ ...prev, file: true }))

    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        setErrors((prev) => ({
          ...prev,
          file: t('corporateIssuerUpload.errors.fileSizeExceeded'),
        }))
        return
      }
      if (isValidFileType(selectedFile)) {
        setFile(selectedFile)
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors.file
          return newErrors
        })
      } else {
        setErrors((prev) => ({
          ...prev,
          file: t('corporateIssuerUpload.errors.invalidFileType'),
        }))
      }
    }
  }

  const isValidFileType = (file: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png']
    return allowedTypes.includes(file.type)
  }

  const handleIssuerChange = (value: string) => {
    setSelectedIssuer(value)
    setTouched((prev) => ({ ...prev, issuer: true }))
    if (value) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.issuer
        return newErrors
      })
    } else {
      setErrors((prev) => ({
        ...prev,
        issuer: t('corporateIssuerUpload.errors.issuerRequired'),
      }))
    }
  }

  const handleSubmit = async () => {
    const formErrors = validateForm()
    setErrors(formErrors)
    setTouched({ issuer: true, file: true })

    if (Object.keys(formErrors).length === 0) {
      try {
        setIsSubmitting(true)

        if (!file) {
          setErrors((prev) => ({
            ...prev,
            file: t('corporateIssuerUpload.errors.invalidCertificate'),
          }))
          setIsSubmitting(false)
          return
        }

        const [objectName] = await uploadCorporateDocuments([
          { file, name: file.name, contentType: file.type },
        ])

        setIncorporationIssuer(selectedIssuer.toLowerCase())
        setCertificateObjectName(objectName)

        nextState()
        navigate(ROUTES.CORPORATE_ARTICLE_INCORPORATION)
      } catch (error) {
        console.error('Error processing certificate:', error)
        setErrors((prev) => ({
          ...prev,
          submit: t('corporateIssuerUpload.errors.submitError'),
        }))
      } finally {
        setIsSubmitting(false)
      }
    }
  }
  const handleKeyDown = async (e: React.KeyboardEvent) => {
    const isFormComplete = selectedIssuer && file && !isSubmitting

    if (e.key === 'Enter' && isFormComplete) {
      e.preventDefault()
      await handleSubmit()
    }
  }

  useEffect(() => {
    const handleDocumentKeyDown = async (e: KeyboardEvent) => {
      const isFormComplete = selectedIssuer && file && !isSubmitting

      if (e.key === 'Enter' && isFormComplete) {
        e.preventDefault()
        await handleSubmit()
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [selectedIssuer, file, isSubmitting])

  return (
    <div
      className="flex flex-col gap-6"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-testid="container-corporate-issuer"
    >
      <div
        className="text-lg font-normal uppercase"
        data-testid="text-corporate-issuer-heading"
      >
        {t('corporateIssuerUpload.title')}
      </div>

      <div
        className="flex flex-col gap-2"
        data-testid="section-corporate-issuer-selector-section"
      >
        <label
          className="text-base"
          data-testid="container-corporate-issuer-label"
        >
          {t('corporateIssuerUpload.issuerLabel')}
        </label>
        <div className="" data-testid="container-corporate-issuer-dropdown">
          <Select.Root
            onValueChange={handleIssuerChange}
            value={selectedIssuer}
            disabled={isNonCanadian}
            data-testid="select-corporate-issuer"
          >
            <Select.Trigger
              className="inline-flex items-center justify-between border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm w-full hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:border-blue-500 dark:text-white disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
              data-testid="select-corporate-issuer-select-trigger"
            >
              <Select.Value
                placeholder={t('corporateIssuerUpload.issuerPlaceholder')}
                data-testid="select-corporate-issuer-select-value"
              />
              <Select.Icon data-testid="select-corporate-issuer-select-icon">
                <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-white" />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content
                className="overflow-hidden bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                data-testid="container-corporate-issuer-select-content"
              >
                <Select.Viewport
                  className="p-0"
                  data-testid="select-corporate-issuer-select-viewport"
                >
                  {issuerGroups.map((group) => (
                    <Select.Group
                      key={group.group}
                      data-testid={`corporate-issuer-group-${group.group.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Select.Label
                        className="px-2 py-1 text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600"
                        data-testid={`corporate-issuer-group-label-${group.group.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {group.group}
                      </Select.Label>
                      {group.options.map((option) => (
                        <Select.Item
                          key={option.value}
                          value={option.value}
                          className="relative flex items-center px-8 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700 cursor-pointer outline-none select-none border-b border-gray-100 dark:border-gray-700 last:border-b-0 dark:text-white"
                          data-testid={`corporate-issuer-option-${option.value}`}
                        >
                          <Select.ItemText
                            data-testid={`corporate-issuer-option-text-${option.value}`}
                          >
                            {option.label}
                          </Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Group>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
        {touched.issuer && errors.issuer && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="error-corporate-issuer"
          >
            {errors.issuer}
          </span>
        )}
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          mt-4 p-8 border-2 border-dashed rounded-lg
          flex flex-col items-center justify-center
          cursor-pointer transition-colors
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${file ? 'bg-green-50' : 'hover:bg-gray-50'}
          ${touched.file && errors.file ? 'border-red-500' : ''}
        `}
        data-testid="container-corporate-issuer-upload"
        onClick={() => document.getElementById('file-upload')?.click()}
      >
        <input
          id="file-upload"
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.jpg,.jpeg,.png"
          data-testid="input-corporate-issuer-file"
        />
        {file ? (
          <div
            className="flex flex-col items-center"
            data-testid="container-corporate-issuer-selected-file"
          >
            <p
              className="text-green-600 font-medium"
              data-testid="text-corporate-issuer-filename"
            >
              {file.name}
            </p>
            <p
              className="text-sm text-gray-500 mt-1"
              data-testid="text-corporate-issuer-change-prompt"
            >
              {t('corporateIssuerUpload.upload.changePrompt')}
            </p>
          </div>
        ) : (
          <div
            className="flex flex-col items-center"
            data-testid="text-corporate-issuer-upload-prompt"
          >
            <p
              className="font-medium"
              data-testid="text-corporate-issuer-upload-heading"
            >
              {t('corporateIssuerUpload.upload.heading')}
            </p>
            <p
              className="text-sm text-gray-500 mt-1"
              data-testid="text-corporate-issuer-upload-instructions"
            >
              {t('corporateIssuerUpload.upload.instructions')}
            </p>
            <p
              className="text-xs text-gray-400 mt-1"
              data-testid="text-corporate-issuer-upload-format-info"
            >
              {t('corporateIssuerUpload.upload.formatInfo')}
            </p>
          </div>
        )}
      </div>
      {touched.file && errors.file && (
        <span
          className="text-sm text-red-500 mt-1"
          data-testid="error-corporate-issuer-file"
        >
          {errors.file}
        </span>
      )}
      {errors.submit && (
        <span
          className="text-sm text-red-500 mt-1"
          data-testid="error-corporate-issuer-submit"
        >
          {errors.submit}
        </span>
      )}

      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || !selectedIssuer || !file}
        className={clsx(
          'mt-4 inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2',
          {
            'opacity-50 cursor-not-allowed bg-gray-400':
              !selectedIssuer || !file || isSubmitting,
            'hover:bg-gray-500': !selectedIssuer || !file || isSubmitting,
          }
        )}
        data-testid="btn-corporate-issuer-submit"
      >
        {isSubmitting
          ? t('corporateIssuerUpload.submitButton.processing')
          : t('corporateIssuerUpload.submitButton.continue')}
      </Button>
    </div>
  )
}

/*
Component Improvement Suggestions:
- Add proper ARIA attributes for accessibility
- Implement form validation error announcements for screen readers
- Add progress indicators for file upload process
- Add file type validation before full upload
- Consider adding file preview functionality
- Include removal option for selected file rather than requiring a new selection
- Display file size for selected files
- Add confirmation dialog for large file uploads
- Improve error handling with more descriptive messages
- Implement retry mechanism for failed uploads
- Add keyboard navigation support for the upload area
- Include loading states during file processing
- Implement chunked uploads for large files
- Improve responsive design for mobile devices
- Add input labels for better accessibility
- Consider using a more accessible custom dropdown implementation
- Add better visual cues for drag and drop boundaries
*/
